import {
  AudioTrackKind,
  sanitizeLanguageCode,
  TextTrackKind,
  TrackId,
} from '@accedo/one-plugin-sdk';

import { TrackNamingBehavior } from '#/service/features/playback/types';

import { tb } from '../translation/translation.store';
import { LanguageMap, LanguagesDesignator } from './languages';

const supportedAudioKinds = ['descriptive_audio'];

/**
 * These labels are commonly used as placeholders for unknown or undefined languages and should not be displayed to users.
 * This includes the CEA-608 closed captioning labels (CC1, CC2, CC3, CC4) which are often used in media tracks but do not represent actual language information.
 */
const invalidDisplayLabels = new Set([
  'und',
  'unknown',
  '',
  'null',
  'undefined',
  'CC1',
  'CC2',
  'CC3',
  'CC4',
]);

export const isTrackLabelInvalid = (label: string | undefined): boolean =>
  !label || invalidDisplayLabels.has(label);

/**
 * Determines whether to use the track's provided label based on the naming behaviour and label validity.
 * @param behaviour - The track naming behaviour setting.
 * @param label - The track's label to evaluate.
 * @returns `true` if the track's label should be used as provided, `false` otherwise.
 */
const isUseAsProvidedLabel = (
  behaviour: TrackNamingBehavior,
  label: string | undefined
): label is string =>
  behaviour === TrackNamingBehavior.UseAsProvided &&
  !isTrackLabelInvalid(label);

/** Gets a nice human-readable name of the audio track */
export const prepareAudioName = (
  track: TrackId,
  behaviour: TrackNamingBehavior = TrackNamingBehavior.Rewrite
) => {
  const { language, kind, label } = track;

  // if the behaviour is use as provided, we should use the track's label.
  if (isUseAsProvidedLabel(behaviour, label)) {
    return label;
  }

  const langName = LanguageMap[language as LanguagesDesignator];

  if (kind && supportedAudioKinds.includes(kind) && langName) {
    return tb(`videoPlayerControlsView.audio.kinds.${kind}`, {
      language: langName,
    });
  }

  if (langName) {
    return langName;
  }

  console.warn(`TrackID ${JSON.stringify(track)} is not currently supported.`);

  return tb('player.audio.track.unknown');
};

export const prepareSubtitleName = (
  track: TrackId,
  behaviour: TrackNamingBehavior = TrackNamingBehavior.Rewrite
) => {
  const { language, kind, label } = track;

  // if the behaviour is use as provided, we should use the track's label.
  if (isUseAsProvidedLabel(behaviour, label)) {
    return label;
  }

  const langName = LanguageMap[language as LanguagesDesignator];

  if (kind && langName) {
    const translationKey =
      kind === TextTrackKind.captions ? 'ccTrackTitle' : 'trackTitle';

    return tb(`videoPlayerControlsView.subtitles.${translationKey}`, {
      subtitlesTrack: { endonym: langName },
    });
  }

  if (langName) {
    return langName;
  }

  console.warn(`Language code "${language}" is not currently supported.`);

  return tb('player.cc.track.unknown');
};

// using language names for sorting, but numbers for the special values to make them sort to the top.
const LanguageSortMap = {
  ...LanguageMap,
  [LanguagesDesignator.off]: '0',
  [LanguagesDesignator.default]: '1',
};

const languageSortName = (trackId: TrackId): string => {
  const languageCode = sanitizeLanguageCode(trackId.language) as LanguagesDesignator;
  const languageName = LanguageSortMap[languageCode] ?? '';
  const kindName =
    trackId.kind === TextTrackKind.captions ? 'cc' : (trackId.kind ?? '');

  return `${languageName}_${kindName}`;
};

export const sortLanguages = (tracks: TrackId[]) =>
  [...tracks].sort((a, b) =>
    languageSortName(a).localeCompare(languageSortName(b))
  );

export const chooseDefaultAudio = (
  availableAudioTracks: TrackId[],
  locale: string
) => {
  // Sort by type, so we get regular audio first
  const kindOrder = [
    AudioTrackKind.regular,
    AudioTrackKind.descriptive,
    undefined,
  ];

  const audioTracks = [...availableAudioTracks];

  audioTracks.sort(
    (a, b) => kindOrder.indexOf(a.kind) - kindOrder.indexOf(b.kind)
  );

  // select first available locale based track, default to first track which should be regular audio if exists
  return (
    audioTracks.find(({ language }) => language === locale) ?? audioTracks[0]
  );
};
